import { useState } from "react";
import {
  BookOpen, Server, Building2, Cable, UploadCloud, QrCode, CheckCircle2,
  ChevronDown, AlertTriangle, Lightbulb, Terminal,
} from "lucide-react";

// Guide de mise en place HotelSIP → mise en prod 3CX.
// Source : deploy/PRODUCTION_RUNBOOK.md, 3CX_V20_SETUP.md, QR_CONTACT_RUNBOOK.md.

function Code({ children }) {
  return (
    <pre className="mt-2 overflow-auto rounded-sm bg-zinc-950 p-3 font-mono text-[11px] leading-relaxed text-zinc-300">
      {children}
    </pre>
  );
}
function Note({ kind = "tip", children }) {
  const map = {
    tip: ["border-blue-200 bg-blue-50 text-blue-900", Lightbulb, "text-blue-500"],
    warn: ["border-amber-300 bg-amber-50 text-amber-900", AlertTriangle, "text-amber-500"],
  };
  const [cls, Icon, ic] = map[kind];
  return (
    <div className={`mt-2 flex gap-2 rounded-sm border ${cls} px-3 py-2 text-xs leading-relaxed`}>
      <Icon className={`mt-0.5 h-3.5 w-3.5 flex-none ${ic}`} />
      <div>{children}</div>
    </div>
  );
}
function Step({ n, title, children }) {
  return (
    <div className="border-t border-zinc-100 px-5 py-4 first:border-t-0">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-zinc-900 text-[11px] font-bold text-white">{n}</span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-900">{title}</div>
          <div className="mt-1 text-[13px] leading-relaxed text-zinc-600">{children}</div>
        </div>
      </div>
    </div>
  );
}

function FlowDiagram() {
  const dests = [
    { x: 14, t1: "Extension", t2: "(réception)" },
    { x: 189, t1: "Groupe d'appel", t2: "(Ring Group)" },
    { x: 364, t1: "File d'attente", t2: "(Queue)" },
    { x: 539, t1: "Réceptionniste", t2: "numérique (SVI)" },
  ];
  return (
    <div className="mb-3 overflow-x-auto rounded-sm border border-zinc-200 bg-zinc-50/50 p-3">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
        Flux d'un appel — touche 9 / Contact QR → 3CX V20
      </div>
      <svg viewBox="0 0 718 230" className="w-full min-w-[600px]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <marker id="g3ah" markerWidth="8" markerHeight="8" refX="5.5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill="#71717A" />
          </marker>
        </defs>
        <rect x="14" y="14" width="200" height="72" rx="7" fill="#fff" stroke="#D4D4D8" />
        <text x="114" y="38" textAnchor="middle" fontSize="13" fontWeight="700" fill="#27272A">Chambre</text>
        <text x="114" y="56" textAnchor="middle" fontSize="10.5" fill="#71717A">Touche 9 ou Contact QR</text>
        <text x="114" y="72" textAnchor="middle" fontSize="10.5" fill="#71717A">cible — ex. 1000</text>
        <line x1="214" y1="50" x2="258" y2="50" stroke="#71717A" strokeWidth="1.5" markerEnd="url(#g3ah)" />
        <rect x="260" y="14" width="200" height="72" rx="7" fill="#fff" stroke="#D4D4D8" />
        <text x="360" y="38" textAnchor="middle" fontSize="13" fontWeight="700" fill="#27272A">HotelSIP · Asterisk</text>
        <text x="360" y="56" textAnchor="middle" fontSize="10.5" fill="#71717A">trunk SIP IP-based</text>
        <text x="360" y="72" textAnchor="middle" fontSize="10.5" fill="#71717A">port 6060</text>
        <line x1="460" y1="50" x2="504" y2="50" stroke="#71717A" strokeWidth="1.5" markerEnd="url(#g3ah)" />
        <text x="482" y="41" textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#2563EB">SDA 1000</text>
        <rect x="506" y="14" width="200" height="72" rx="7" fill="#F3ECDD" stroke="#D9C9A3" />
        <text x="606" y="38" textAnchor="middle" fontSize="13" fontWeight="700" fill="#27272A">3CX V20</text>
        <text x="606" y="56" textAnchor="middle" fontSize="10.5" fill="#8A6D38">Règle entrante</text>
        <text x="606" y="72" textAnchor="middle" fontSize="10.5" fill="#8A6D38">(match SDA / DID)</text>
        <line x1="606" y1="86" x2="606" y2="120" stroke="#71717A" strokeWidth="1.5" />
        <line x1="96.5" y1="120" x2="621.5" y2="120" stroke="#71717A" strokeWidth="1.5" />
        {dests.map((d) => {
          const cx = d.x + 82.5;
          return (
            <g key={d.t1}>
              <line x1={cx} y1="120" x2={cx} y2="150" stroke="#71717A" strokeWidth="1.5" markerEnd="url(#g3ah)" />
              <rect x={d.x} y="152" width="165" height="56" rx="7" fill="#F3ECDD" stroke="#E5D9BF" />
              <text x={cx} y="176" textAnchor="middle" fontSize="11.5" fontWeight="600" fill="#27272A">{d.t1}</text>
              <text x={cx} y="192" textAnchor="middle" fontSize="10" fill="#8A6D38">{d.t2}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

const PHASES = [
  {
    id: "serveur", icon: Server, title: "Serveur Asterisk (infra)",
    sub: "Déployer le VPS qui héberge l'Asterisk de l'hôtel et le rattacher à HotelSIP.",
    body: (
      <>
        <Step n="1" title="Générer le script d'installation">
          Menu <b>Serveurs SIP → Ajouter un serveur</b> : renseigne nom + IP publique du VPS, puis
          télécharge le script <code>install_asterisk.sh</code> généré.
          <Note>VPS Debian/Ubuntu récent, 2 vCPU / 2 Go, IP publique fixe, accès root SSH.</Note>
        </Step>
        <Step n="2" title="Lancer le script sur le VPS (en root)">
          Exporte l'IP de la plateforme + le LAN/VPN à whitelister (fail2ban), puis lance :
          <Code>{`HOTELSIP_IP='193.105.165.37' \\
IGNORE_IPS='10.0.0.0/8 192.168.0.0/16' \\
AMI_USER=hotelsip AMI_SECRET='<fort>' AGENT_TOKEN='<fort>' \\
bash install_asterisk.sh`}</Code>
          <Note kind="warn"><b>IGNORE_IPS</b> = CIDRs du LAN hôtel + VPN. Sans eux, fail2ban finit par bannir les téléphones des chambres.</Note>
        </Step>
        <Step n="3" title="Le script s'auto-vérifie">
          En fin d'exécution, le bloc de contrôle teste Asterisk / agent / coturn / fail2ban,
          les modules (res_pjsip, ConfBridge), le transport SIP, le <code>/ws</code> et la santé de l'agent.
          Il affiche <b>RESULTAT : OK</b> ou échoue (exit 1) si un point est down.
        </Step>
        <Step n="4" title="Rattacher le serveur dans HotelSIP">
          <b>Serveurs SIP → Ajouter</b> : IP = VPS, Port SIP = 6060, AMI (host/port 5938/user/secret),
          Agent URL = <code>https://VPS:9443</code> + jeton. Puis <b>Tester</b> → « AMI joignable ».
        </Step>
      </>
    ),
  },
  {
    id: "hotel", icon: Building2, title: "Créer l'hôtel",
    sub: "Déclarer l'hôtel, choisir son mode de contact et son trunk.",
    body: (
      <>
        <Step n="1" title="Nouvel hôtel">
          <b>Hôtels → Ajouter</b> : nom, domaine SIP, serveur rattaché, ville. Choisis le <b>Mode contact</b> :
          <i> Téléphones</i> (postes en chambre), <i>100% QR</i> (sans poste, portail web), ou <i>Téléphones + QR</i>.
        </Step>
        <Step n="2" title="Choisir le trunk (fiche hôtel → réglage Trunk)">
          <b>Dédié</b> : l'hôtel a son propre 3CX. <b>Partagé</b> : plusieurs hôtels partagent un 3CX
          multi-tenant (page <b>3CX Multi-tenant</b>) — extensions globalement uniques par client.
          <Note kind="warn">Un hôtel ne doit jamais avoir un trunk dédié <b>et</b> partagé vers la même IP 3CX (conflit d'identification). Le passage en « Partagé » retire automatiquement le dédié au push.</Note>
        </Step>
        <Step n="3" title="Chambres (mode Téléphones/Both)">
          Onglet <b>Chambres</b> : ajout simple ou en lot (extension = n° de chambre), MAC + modèle du poste
          pour le provisioning. En mode 100% QR, pas de poste : seul le n° de chambre + son QR comptent.
        </Step>
      </>
    ),
  },
  {
    id: "trunk3cx", icon: Cable, title: "Trunk côté 3CX",
    sub: "Connecter le PBX 3CX de l'hôtel à l'Asterisk HotelSIP (trunk SIP IP-based).",
    body: (
      <>
        <Step n="1" title="Importer le modèle de trunk">
          Console 3CX → <b>Voix & Chat → Trunks SIP → Ajouter → Importer un modèle</b> :
          <code> 3cx/HotelSIP-Asterisk-Trunk-V20.xml</code>. Le fournisseur « HotelSIP — Asterisk Trunk (IP Based) » apparaît.
          <Note>Ce modèle lit le nom de l'appelant dans le <code>From: Display Name</code> → affiche « Chambre 201 » au lieu de « 201 ».</Note>
        </Step>
        <Step n="2" title="Créer le trunk (onglet Général)">
          <div className="mt-1 overflow-hidden rounded-sm border border-zinc-200">
            <table className="w-full text-xs">
              <tbody>
                {[
                  ["Hostname / IP (Registrar & Proxy)", "IP publique de l'Asterisk HotelSIP"],
                  ["Port SIP", "6060 (durci ; 5060 si standard)"],
                  ["Authentification", "Ne pas requérir — Basé sur IP"],
                  ["IP autorisée", "IP publique de l'Asterisk HotelSIP"],
                  ["Transport", "UDP"],
                  ["Appels simultanés", "selon le nb de chambres (ex. 30)"],
                ].map(([k, v]) => (
                  <tr key={k} className="border-b border-zinc-100 last:border-0">
                    <td className="bg-zinc-50/60 px-3 py-1.5 font-medium text-zinc-700">{k}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-600">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Note>IP-based : le trunk n'affiche pas « Registered », c'est normal. Vérifie juste qu'il est <b>actif, sans erreur</b>.</Note>
        </Step>
        <Step n="3" title="Router les SDA entrantes (réception, groupe, file…)">
          <FlowDiagram />
          Quand un client appuie sur la <b>touche 9</b> (ou via le QR), HotelSIP envoie l'appel au 3CX
          vers le numéro défini comme <b>cible</b> (sur la touche type 9 ou la destination Contact QR).
          Côté 3CX, ce numéro est la <b>SDA</b> (Sélection Directe à l'Arrivée / DID). Une <b>règle
          entrante</b> 3CX fait correspondre cette SDA à une destination — au choix :
          <div className="mt-2 overflow-hidden rounded-sm border border-zinc-200">
            <table className="w-full text-xs">
              <tbody>
                {[
                  ["Extension", "Un poste unique — ex. la réception 1000. Le plus simple."],
                  ["Groupe d'appel (Ring Group)", "Sonne plusieurs postes en même temps (réception + conciergerie) ; le premier qui décroche prend l'appel."],
                  ["File d'attente (Queue)", "Mise en attente avec musique + distribution aux agents disponibles (réception à plusieurs, nuit, débordement). Stats incluses."],
                  ["Réceptionniste numérique (SVI/IVR)", "Menu vocal : « Tapez 1 pour la réception, 2 pour le restaurant… »."],
                  ["Boîte vocale / n° externe", "Renvoi vers la messagerie ou un mobile (gérant) la nuit / hors-heures."],
                ].map(([k, v]) => (
                  <tr key={k} className="border-b border-zinc-100 last:border-0 align-top">
                    <td className="w-44 bg-zinc-50/60 px-3 py-1.5 font-medium text-zinc-700">{k}</td>
                    <td className="px-3 py-1.5 text-[11px] leading-relaxed text-zinc-600">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2 rounded-sm border border-zinc-200 bg-white p-3">
            <div className="text-xs font-bold text-zinc-900">Concrètement — ex. touche 9 → 1000 (3CX V20)</div>
            <ol className="mt-1.5 space-y-1.5 text-[13px] leading-relaxed text-zinc-600">
              <li><b>1. Déclarer la SDA sur le trunk</b> — fiche du <b>Trunk SIP HotelSIP</b> → onglet
                <b> DIDs / DDIs</b> → ajouter le numéro <code>1000</code> (et chaque autre cible : <code>1001</code>, <code>1002</code>…).
                Sans ça, 3CX ne reconnaît pas le numéro entrant.</li>
              <li><b>2. Router via une règle entrante</b> — Voix & Chat → <b>Règles entrantes → Ajouter</b> →
                trunk HotelSIP, <b>DID = 1000</b> → <b>Destination</b> = l'<b>utilisateur/extension 1000</b>,
                <i> ou</i> une <b>file d'attente</b>, un <b>groupe d'appel</b>, un <b>SVI</b>… (jour/nuit possibles).</li>
              <li><b>3. Faire correspondre</b> — le <b>numéro cible</b> côté HotelSIP (touche 9 ou destination QR)
                doit être <b>exactement</b> ce DID <code>1000</code>.</li>
            </ol>
          </div>
          <Note kind="warn">La destination doit déjà <b>exister</b> côté 3CX : créer l'<b>utilisateur/extension 1000</b>
          (ou la <b>file d'attente</b> / le <b>groupe d'appel</b>) <b>avant</b> de l'attribuer dans la règle entrante.
          Un numéro cible non déclaré en DID/SDA sur le trunk n'est jamais routé.</Note>
          <Note kind="tip"><b>Multi-services :</b> pointe chaque service vers une SDA différente —
          <code> 1000</code> = réception (extension), <code>1001</code> = restaurant (file d'attente),
          <code> 1002</code> = spa (groupe d'appel)… Tu définis simplement la <b>cible</b> côté HotelSIP
          (touche 9 / destination QR) = la <b>SDA</b> côté 3CX, et 3CX route chacune vers la bonne destination.</Note>
        </Step>
        <Step n="4" title="Règle sortante (3CX → chambres)">
          <b>Règles sortantes → Ajouter</b> : critère = numéros de longueur 3 commençant par <code>1</code> (chambres <code>1xx</code>),
          route via le trunk HotelSIP, <b>Strip = 0</b>, pas de préfixe.
        </Step>
        <Step n="5" title="Affichage « Chambre 201 »">
          Le modèle importé le fait déjà (CallerName ← <code>From: Display Name</code>, CallerNum ← <code>From: User Part</code>).
          Avec un autre modèle, règle-le manuellement dans <b>Inbound Parameters</b>.
        </Step>
      </>
    ),
  },
  {
    id: "push", icon: UploadCloud, title: "Pousser & provisionner",
    sub: "Écrire la config sur l'Asterisk et provisionner postes / QR.",
    body: (
      <>
        <Step n="1" title="Pousser la config">
          Fiche hôtel → <b>Pousser vers serveur</b> : écrit pjsip/extensions sur le VPS + reload
          (« Configuration poussée et rechargée »).
        </Step>
        <Step n="2" title="Provisionner les postes (mode Téléphones)">
          Onglet Chambres → <b>Provisioning postes</b> (import MAC en lot) puis <b>Provisionner les postes</b>.
          Les Cisco/Yealink prennent leur config par MAC.
        </Step>
        <Step n="3" title="Générer les QR (mode QR/Both)">
          Onglet Chambres → bouton <b>QR</b> par chambre, ou export PDF. Le QR encode
          <code> https://hotelsip.netsip.tel/c/&#123;token&#125;</code>.
        </Step>
        <Step n="4" title="Synchroniser le statut">
          <b>Synchroniser statut</b> : les postes enregistrés passent à « Enregistré ».
        </Step>
      </>
    ),
  },
  {
    id: "qr", icon: QrCode, title: "Contact QR (mode QR / Both)",
    sub: "Les services joignables par l'invité depuis le QR de sa chambre.",
    body: (
      <>
        <Step n="1" title="Définir les destinations">
          Onglet <b>Contact QR</b> : ajoute Réception, Restaurant, Spa… (libellé FR/EN, cible = extension 3CX, horaires).
        </Step>
        <Step n="2" title="Lier à une touche type 9 (recommandé)">
          La <b>touche type 9</b> porte l'extension 3CX (source). Sur la destination QR, choisis « Touche liée » →
          sa cible suit la touche (lien live). On définit l'extension <b>une seule fois</b>.
        </Step>
        <Step n="3" title="Pousser & tester">
          <b>Pousser</b>, puis scanne un QR : le menu s'affiche, l'appel WebRTC sonne la réception (pont ConfBridge),
          et le 3CX voit « Chambre &lt;n&gt; ».
          <Note>Le pont invité↔réception passe obligatoirement par ConfBridge (déjà posé par le script d'install). Audio bidirectionnel validé iOS/Android.</Note>
        </Step>
      </>
    ),
  },
  {
    id: "recette", icon: CheckCircle2, title: "Recette & mise en prod",
    sub: "Valider de bout en bout avant de livrer à l'hôtel.",
    body: (
      <>
        <Step n="1" title="Tests fonctionnels">
          <ul className="ml-4 list-disc space-y-0.5">
            <li>Chambre → composer <b>9</b> → la réception (1000) sonne, audio 2 sens, « Chambre &lt;n&gt; » affiché.</li>
            <li>Réception → composer une chambre (ex. 101) → la chambre sonne.</li>
            <li>QR (si activé) → audio bidirectionnel Android Chrome <b>et</b> iOS Safari.</li>
            <li>Destination interdite → rejetée ; 6ᵉ requête session &lt; 60 s → 429 (rate-limit).</li>
          </ul>
        </Step>
        <Step n="2" title="Diagnostic côté Asterisk">
          <Code>{`asterisk -rx "pjsip show contacts"        # postes enregistrés
asterisk -rx "pjsip show registrations"   # statut trunk 3CX
asterisk -rx "dialplan show hotel-<slug>"
# CDR : /var/log/asterisk/cdr-csv/Master.csv  (ou onglet Appels (CDR))`}</Code>
        </Step>
        <Step n="3" title="Go-live">
          Onglet <b>Appels (CDR) → Actualiser</b> : les appels de test apparaissent. Trunk 3CX « actif sans erreur ».
          → l'hôtel est en production.
        </Step>
      </>
    ),
  },
];

export default function Guide3cx() {
  const [open, setOpen] = useState("serveur");
  return (
    <div className="mx-auto max-w-3xl pb-16">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-zinc-900">
          <BookOpen className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="font-head text-xl font-black tracking-tight text-zinc-900">Guide de mise en place 3CX</h1>
          <p className="text-sm text-zinc-500">Du serveur Asterisk à la mise en production côté 3CX — chaque étape, dans l'ordre.</p>
        </div>
      </div>

      <ol className="mb-6 flex flex-wrap gap-2">
        {PHASES.map((p, i) => (
          <li key={p.id}>
            <button onClick={() => setOpen(p.id)}
              className={`inline-flex items-center gap-1.5 rounded-sm border px-2.5 py-1 text-xs font-medium transition-colors ${open === p.id ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300"}`}>
              <span className="font-mono">{i + 1}</span> {p.title}
            </button>
          </li>
        ))}
      </ol>

      <div className="space-y-3">
        {PHASES.map((p, i) => {
          const Icon = p.icon;
          const isOpen = open === p.id;
          return (
            <section key={p.id} data-testid={`guide-phase-${p.id}`} className="overflow-hidden rounded-sm border border-zinc-200 bg-white">
              <button onClick={() => setOpen(isOpen ? "" : p.id)} className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-zinc-50">
                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-sm bg-brass-tint text-zinc-700" style={{ background: "#F3ECDD" }}>
                  <Icon className="h-4 w-4 text-zinc-700" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-zinc-900"><span className="mr-1.5 font-mono text-zinc-400">{i + 1}.</span>{p.title}</div>
                  <div className="truncate text-xs text-zinc-500">{p.sub}</div>
                </div>
                <ChevronDown className={`h-4 w-4 flex-none text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && <div className="border-t border-zinc-200">{p.body}</div>}
            </section>
          );
        })}
      </div>

      <p className="mt-8 flex items-center gap-1.5 text-xs text-zinc-400">
        <Terminal className="h-3.5 w-3.5" /> Réfs internes : deploy/PRODUCTION_RUNBOOK.md · 3CX_V20_SETUP.md · QR_CONTACT_RUNBOOK.md
      </p>
    </div>
  );
}
